import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

// APIs
import ComplianceAlertClient from "@common/api/ComplianceAlertClient";

// Helpers
import { handleError } from "../helpers/error";
import useLogin from "./useLogin";

// Actions
import LoadingAction from "@common/redux/actions/LoadingAction";
import ComplianceAlertsAction from "@common/redux/actions/ComplianceAlertsAction";

// Constants
import useSessionModal from "./useSessionModal";
import useLinkedAlert from "./useLinkedAlert";
import useIntermediaryModal from "./useIntermediaryModal";

const useComplianceAlerts = () => {
  const history = useHistory();
  const [{ siteGuid }] = useLogin();

  const data = useSelector((state) =>
    ComplianceAlertsAction.getStatePath(state),
  );

  const {
    selectedAlerts,
    datesRange,
    selectedAlertType,
    activePage,
    alertData,
    selectAllAlerts,
    sortColumns,
  } = data;

  const { fetchAlertSessions } = useSessionModal();
  const {
    fetchAlertsByIds,
    fetchAlertConfig,
    redirectAlertToSuspiciousActivity,
    redirectAlertToObservations,
    redirectAlertToCustomerInteraction,
    redirectAlertToCustomerIncidents,
  } = useLinkedAlert();

  const { openFacialMatchAlert } = useIntermediaryModal();

  const setState = ComplianceAlertsAction.dispatch;

  const onFormLoad = () => {
    ComplianceAlertClient.getMetaData().then(
      ({ alertTypes }) => {
        setState({ alertTypes: [{ id: null, name: "All" }, ...alertTypes] });
      },
      (error) => handleError(error),
    );

    return () => {
      ComplianceAlertsAction.reset();
    };
  };

  const onPageChange = (_, { activePage }) => setState({ activePage });

  const clearAlerts = (complianceAlertIds, reloadAlerts = true) => {
    ComplianceAlertClient.clearAlerts({
      ids: complianceAlertIds,
    })
      .then((response) => {
        setState({ selectedAlerts: [] });
        reloadAlerts && onSearch();
      })
      .catch(handleError);
  };

  const onClear = () => {
    LoadingAction.set(true)
      .then(() => clearAlerts(selectedAlerts))
      .catch(handleError)
      .finally(() => LoadingAction.set(false));
  };

  const onSearch = (useLoader = false) => {
    const [alertTimeFrom, alertTimeTo] = datesRange
      ? datesRange.split(" - ")
      : [null, null];

    const payload = {
      alertTypeId: selectedAlertType ?? 0,
      sortColumns: sortColumns.length > 0 ? [...sortColumns] : [],
      siteGuid,
      ...(alertTimeFrom && { alertTimeFrom }),
      ...(alertTimeTo && { alertTimeTo }),
      pageNumber: activePage,
    };

    const callback = () => {
      ComplianceAlertClient.getAlerts(payload)
        .then(({ totalRecords, data }) => {
          setState({ alertData: data, totalRecords });
        })
        .catch(handleError)
        .finally(() => LoadingAction.set(false));
    };

    // load for view, but not for alerts count button
    if (useLoader) {
      LoadingAction.set(true).then(callback);
    } else {
      callback();
    }
  };

  const handleBack = () => {
    history.push("/compliance");
  };

  const handleChange = (_, { name, value }) => {
    setState({ [name]: value });
    if (name === "selectedAlertType" || name === "datesRange") {
      setState({ activePage: 1 });
    }
  };

  const handleSelect = (event, alertId) => {
    event.preventDefault();
    event.stopPropagation();

    const newSelectedAlerts = selectedAlerts.includes(alertId)
      ? selectedAlerts.filter((id) => id !== alertId)
      : [...selectedAlerts, alertId];

    setState({ selectedAlerts: newSelectedAlerts });
  };

  const handleSelectAll = () => {
    const newSelectAll = !selectAllAlerts;
    setState({ selectAllAlerts: newSelectAll });

    // Create a new array with all alert IDs or empty array
    const newSelectedAlerts = newSelectAll
      ? alertData.map((alert) => alert.id)
      : [];

    setState({ selectedAlerts: newSelectedAlerts });
  };

  const isAnyAlertSelected = Object.values(selectedAlerts).some(
    (value) => value,
  );

  const onAlertClick = async (alert) => {
    const frsEvent = alert.frsEventTypeId;
    if (frsEvent === 1 || frsEvent === 2) {
      const confirmed = await openFacialMatchAlert(alert);
      if (confirmed) {
        await handleAlert(alert);
      } else {
        LoadingAction.set(true)
          .then(() => clearAlerts([alert.id]))
          .catch(handleError)
          .finally(() => LoadingAction.set(false));
      }
    } else {
      await handleAlert(alert);
    }
  };

  const handleAlert = async (alert) => {
    const sessionAlerts =
      alertData?.length > 0 &&
      alertData?.filter((x) => x.sessionGuid === alert.sessionGuid);

    // const isInternalSelfExclude = alert?.alertType?.name?.includes("Internal Self Exclusion");

    const alertIds = sessionAlerts?.map((alert) => alert.id).join(",");

    fetchAlertsByIds(alertIds);
    alert.sessionGuid && fetchAlertSessions(2, alert.sessionGuid);

    const alertConfig = await fetchAlertConfig();


    // If there are more than one alerts, use bundled event type
    // const eventType =
    //   sessionAlerts?.length > 1
    //     ? alertConfig?.record?.defaultBundledEventTypeId
    //     : alert?.alertType?.eventType?.id;
    // const incidentType =
    //   sessionAlerts?.length > 1
    //     ? alertConfig?.record?.defaultBundledIncidentTypeId
    //     : alert?.alertType?.incidentType?.id;

    const eventType = alert?.alertType?.eventType?.id;
    const incidentType = alert?.alertType?.incidentType?.id;

    if (eventType === 3 && incidentType === 3) {
      redirectAlertToSuspiciousActivity(alert);
    } else if (eventType === 3 && incidentType === 2) {
      redirectAlertToCustomerIncidents(alert);
    } else if (eventType === 4) {
      redirectAlertToObservations(alert);
    } else if (eventType === 2) {
      redirectAlertToCustomerInteraction(alert);
    } else if (eventType === 5) {
      history.push("/compliance/self-exclusion");
    }
  };

  return {
    ...data,
    setState,
    onFormLoad,
    clearAlerts,
    onClear,
    onSearch,
    onPageChange,
    handleBack,
    handleChange,
    handleSelect,
    handleSelectAll,
    isAnyAlertSelected,
    onAlertClick,
  };
};

export default useComplianceAlerts;
